import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "atencao-empty"
}
const _hoisted_2 = {
  key: 1,
  class: "atencao-empty"
}
const _hoisted_3 = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "1.5",
  style: {"color":"#22c55e"}
}
const _hoisted_4 = { class: "sla-tab-summary" }
const _hoisted_5 = { class: "sla-sum-badge breach" }
const _hoisted_6 = { class: "sla-tab-grid" }
const _hoisted_7 = { class: "sla-tcard-fluxo" }
const _hoisted_8 = { class: "sla-tcard-profile" }
const _hoisted_9 = { class: "agrd-card-head" }
const _hoisted_10 = ["onMouseenter"]
const _hoisted_11 = ["src", "alt"]
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "agrd-contact" }
const _hoisted_14 = { class: "agrd-name" }
const _hoisted_15 = {
  key: 0,
  class: "agrd-phone"
}
const _hoisted_16 = { class: "sla-head-right" }
const _hoisted_17 = { class: "agrd-id" }
const _hoisted_18 = { class: "agrd-card-body" }
const _hoisted_19 = { class: "agrd-row" }
const _hoisted_20 = { class: "agrd-row" }
const _hoisted_21 = { class: "sla-tcard-metrics" }
const _hoisted_22 = { class: "sla-mbadge-body" }
const _hoisted_23 = { class: "sla-mbadge-val" }
const _hoisted_24 = { class: "sla-mbadge-lbl" }
const _hoisted_25 = {
  key: 0,
  class: "agrd-labels"
}
const _hoisted_26 = { class: "agrd-card-foot" }
const _hoisted_27 = ["onClick"]

import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { avatarColor, initials, fluxoLabel, fmtChannel } from '@/composables/useHelpers'
import { requestOpenChat } from '@/composables/useChatTrigger'
import { useSLA, type SLAProfile } from '@/composables/useSLA'

interface SLAMetric {
  type: 'firstResponse' | 'resolution' | 'redirect'
  label: string
  startTime: number
  deadline: number
  totalSecs: number
  status: 'breached' | 'atRisk' | 'ok'
  remainingSecs: number
}

interface SLAItem {
  conv: Conversation
  profile: SLAProfile
  metrics: SLAMetric[]
  worstStatus: 'breached' | 'atRisk'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SLATab',
  props: {
    conversations: {},
    active: { type: Boolean }
  },
  emits: ["updateBadge", "showPopup", "hidePopup"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { profiles, slaLoaded, loadProfiles, getProfileForFluxo } = useSLA()

const now = ref(Math.floor(Date.now() / 1000))
let ticker: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  loadProfiles()
  ticker = setInterval(() => { now.value = Math.floor(Date.now() / 1000) }, 30_000)
})
onUnmounted(() => { if (ticker) clearInterval(ticker) })

// ── SLA Computation ──────────────────────────────────────
function metricStatus(deadline: number, notifyBefore: number, nowSecs: number): 'breached' | 'atRisk' | 'ok' {
  if (nowSecs >= deadline) return 'breached'
  if (nowSecs >= deadline - notifyBefore * 60) return 'atRisk'
  return 'ok'
}

const slaItems = computed<SLAItem[]>(() => {
  const nowSecs = now.value
  return props.conversations
    .filter(c => (c.status === 'open' || c.status === 'pending') && !c.labels.includes('avaliacao_atendimento'))
    .flatMap(conv => {
      const profile = getProfileForFluxo(conv.fluxo_id)
      if (!profile) return []

      const metrics: SLAMetric[] = []

      if (profile.firstResponseTime > 0 && conv.waiting_since > 0) {
        const deadline = conv.waiting_since + profile.firstResponseTime * 60
        const st = metricStatus(deadline, profile.notifyBefore, nowSecs)
        if (st !== 'ok') metrics.push({
          type: 'firstResponse', label: '1ª Resposta',
          startTime: conv.waiting_since, deadline,
          totalSecs: profile.firstResponseTime * 60,
          status: st, remainingSecs: deadline - nowSecs,
        })
      }

      if (profile.resolutionTime > 0) {
        const deadline = conv.created_at + profile.resolutionTime * 60
        const st = metricStatus(deadline, profile.notifyBefore, nowSecs)
        if (st !== 'ok') metrics.push({
          type: 'resolution', label: 'Resolução',
          startTime: conv.created_at, deadline,
          totalSecs: profile.resolutionTime * 60,
          status: st, remainingSecs: deadline - nowSecs,
        })
      }

      if (profile.redirectTime > 0 && conv.waiting_since > 0) {
        const deadline = conv.waiting_since + profile.redirectTime * 60
        const st = metricStatus(deadline, profile.notifyBefore, nowSecs)
        if (st !== 'ok') metrics.push({
          type: 'redirect', label: 'Redirecionamento',
          startTime: conv.waiting_since, deadline,
          totalSecs: profile.redirectTime * 60,
          status: st, remainingSecs: deadline - nowSecs,
        })
      }

      if (!metrics.length) return []

      const worstStatus: 'breached' | 'atRisk' = metrics.some(m => m.status === 'breached') ? 'breached' : 'atRisk'
      if (worstStatus !== 'breached') return []
      return [{ conv, profile, metrics, worstStatus }]
    })
    .sort((a, b) => {
      if (a.worstStatus !== b.worstStatus) return a.worstStatus === 'breached' ? -1 : 1
      const aMin = Math.min(...a.metrics.map(m => m.remainingSecs))
      const bMin = Math.min(...b.metrics.map(m => m.remainingSecs))
      return aMin - bMin
    })
})

const breachedCount = computed(() => slaItems.value.length)

watchEffect(() => emit('updateBadge', slaItems.value.length))

function showAvPopup(conv: Conversation, e: MouseEvent) {
  emit('showPopup', {
    name:      conv.contact.name,
    role:      'Contato',
    phone:     conv.contact.phone || undefined,
    thumbnail: conv.contact.thumbnail || undefined,
    channel:   fmtChannel(conv.channel),
    team:      conv.team?.name || null,
    status:    conv.status,
    labels:    conv.labels,
  }, e.currentTarget as HTMLElement)
}

function getBarPct(m: SLAMetric): number {
  if (!m.startTime || !m.totalSecs) return 0
  const elapsed = now.value - m.startTime
  return Math.min(100, Math.max(0, (elapsed / m.totalSecs) * 100))
}

function formatDur(secs: number): string {
  if (secs < 0) secs = 0
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min ${s < 10 ? '0' : ''}${s}s`
  return `${s}s`
}

function resM(item: SLAItem): SLAMetric | null {
  return item.metrics.find(m => m.type === 'resolution') || null
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["tab-pane sla-pane", { hidden: !__props.active }])
  }, [
    (_unref(slaLoaded) && !_unref(profiles).length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _cache[5] || (_cache[5] = _createElementVNode("svg", {
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "1.5"
          }, [
            _createElementVNode("circle", {
              cx: "12",
              cy: "12",
              r: "10"
            }),
            _createElementVNode("polyline", { points: "12 6 12 12 16 14" })
          ], -1)),
          _cache[6] || (_cache[6] = _createElementVNode("p", null, "Nenhum perfil de SLA configurado", -1)),
          _createElementVNode("small", null, [
            _cache[3] || (_cache[3] = _createTextVNode("Configure perfis de SLA na página ", -1)),
            _createVNode(_component_router_link, { to: "/sla" }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode("SLA", -1)
              ]))]),
              _: 1
            }),
            _cache[4] || (_cache[4] = _createTextVNode(" para monitorar acordos.", -1))
          ])
        ]))
      : (!slaItems.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (_openBlock(), _createElementBlock("svg", _hoisted_3, [...(_cache[7] || (_cache[7] = [
              _createElementVNode("circle", {
                cx: "12",
                cy: "12",
                r: "10"
              }, null, -1),
              _createElementVNode("polyline", { points: "20 6 9 17 4 12" }, null, -1)
            ]))])),
            _cache[8] || (_cache[8] = _createElementVNode("p", { style: {"color":"#15803d"} }, "Todos os SLAs dentro do prazo", -1)),
            _cache[9] || (_cache[9] = _createElementVNode("small", null, "Conversas em risco ou com SLA estourado aparecerão aqui.", -1))
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("span", _hoisted_5, [
                _cache[10] || (_cache[10] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.5",
                  "stroke-linecap": "round"
                }, [
                  _createElementVNode("path", { d: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" }),
                  _createElementVNode("line", {
                    x1: "12",
                    y1: "9",
                    x2: "12",
                    y2: "13"
                  }),
                  _createElementVNode("line", {
                    x1: "12",
                    y1: "17",
                    x2: "12.01",
                    y2: "17"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(breachedCount.value) + " SLA" + _toDisplayString(breachedCount.value !== 1 ? 's' : '') + " estourado" + _toDisplayString(breachedCount.value !== 1 ? 's' : ''), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slaItems.value, (item) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: item.conv.id,
                  class: _normalizeClass(['sla-tcard', item.worstStatus])
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass(['sla-tcard-bar', item.worstStatus])
                  }, [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(fluxoLabel)(item.conv.fluxo_id)), 1),
                    _createElementVNode("span", _hoisted_8, _toDisplayString(item.profile.name), 1),
                    _createElementVNode("span", {
                      class: _normalizeClass(['sla-tcard-status', item.worstStatus])
                    }, _toDisplayString(item.worstStatus === 'breached' ? 'ESTOURADO' : 'EM RISCO'), 3)
                  ], 2),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", {
                      class: "agrd-av",
                      style: _normalizeStyle({ background: _unref(avatarColor)(item.conv.contact.name) }),
                      onMouseenter: ($event: any) => (showAvPopup(item.conv, $event)),
                      onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (emit('hidePopup')))
                    }, [
                      (item.conv.contact.thumbnail)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: item.conv.contact.thumbnail,
                            alt: item.conv.contact.name,
                            style: {"width":"100%","height":"100%","object-fit":"cover","border-radius":"50%","display":"block"},
                            onError: _cache[0] || (_cache[0] = (e) => (e.target as HTMLImageElement).style.display='none')
                          }, null, 40, _hoisted_11))
                        : (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(_unref(initials)(item.conv.contact.name)), 1))
                    ], 44, _hoisted_10),
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, _toDisplayString(item.conv.contact.name), 1),
                      (item.conv.contact.phone)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(item.conv.contact.phone), 1))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      (resM(item))
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: _normalizeClass(['sla-res-tag', resM(item)!.status])
                          }, [
                            _cache[11] || (_cache[11] = _createElementVNode("svg", {
                              width: "11",
                              height: "11",
                              viewBox: "0 0 24 24",
                              fill: "none",
                              stroke: "currentColor",
                              "stroke-width": "2.5",
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round"
                            }, [
                              _createElementVNode("circle", {
                                cx: "12",
                                cy: "12",
                                r: "10"
                              }),
                              _createElementVNode("polyline", { points: "12 6 12 12 16 14" })
                            ], -1)),
                            _createElementVNode("span", null, _toDisplayString(resM(item)!.status === 'breached' ? '+' + formatDur(-resM(item)!.remainingSecs) : formatDur(resM(item)!.remainingSecs)), 1)
                          ], 2))
                        : _createCommentVNode("", true),
                      _createElementVNode("span", _hoisted_17, "#" + _toDisplayString(item.conv.id), 1)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("div", _hoisted_19, [
                      _cache[12] || (_cache[12] = _createStaticVNode("<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" data-v-a34c1838><path d=\"M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2\" data-v-a34c1838></path><circle cx=\"9\" cy=\"7\" r=\"4\" data-v-a34c1838></circle><path d=\"M23 21v-2a4 4 0 0 0-3-3.87\" data-v-a34c1838></path><path d=\"M16 3.13a4 4 0 0 1 0 7.75\" data-v-a34c1838></path></svg>", 1)),
                      _createElementVNode("span", {
                        class: _normalizeClass({ 'agrd-muted': !item.conv.team })
                      }, _toDisplayString(item.conv.team?.name || 'TypeBot'), 3)
                    ]),
                    _createElementVNode("div", _hoisted_20, [
                      _cache[13] || (_cache[13] = _createElementVNode("svg", {
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      }, [
                        _createElementVNode("path", { d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" }),
                        _createElementVNode("circle", {
                          cx: "12",
                          cy: "7",
                          r: "4"
                        })
                      ], -1)),
                      _createElementVNode("span", {
                        class: _normalizeClass({ 'agrd-muted': !item.conv.assignee })
                      }, _toDisplayString(item.conv.assignee?.name || '— sem agente'), 3)
                    ]),
                    _createElementVNode("div", _hoisted_21, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.metrics, (m) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: m.type,
                          class: _normalizeClass(['sla-mbadge', m.status])
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(['sla-mbadge-ico', m.status])
                          }, [...(_cache[14] || (_cache[14] = [
                            _createElementVNode("svg", {
                              width: "13",
                              height: "13",
                              viewBox: "0 0 24 24",
                              fill: "none",
                              stroke: "currentColor",
                              "stroke-width": "2.5",
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round"
                            }, [
                              _createElementVNode("circle", {
                                cx: "12",
                                cy: "12",
                                r: "10"
                              }),
                              _createElementVNode("polyline", { points: "12 6 12 12 16 14" })
                            ], -1)
                          ]))], 2),
                          _createElementVNode("div", _hoisted_22, [
                            _createElementVNode("span", _hoisted_23, [
                              (m.status === 'breached')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createTextVNode("+" + _toDisplayString(formatDur(-m.remainingSecs)), 1)
                                  ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createTextVNode(_toDisplayString(formatDur(m.remainingSecs)), 1)
                                  ], 64))
                            ]),
                            _createElementVNode("span", _hoisted_24, _toDisplayString(m.label), 1)
                          ])
                        ], 2))
                      }), 128))
                    ]),
                    (item.conv.labels.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.conv.labels, (lbl) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: lbl,
                              class: "label-chip"
                            }, _toDisplayString(lbl.replace(/-|_/g, ' ')), 1))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_26, [
                    _createElementVNode("button", {
                      class: _normalizeClass(['sla-view-btn', item.worstStatus]),
                      onClick: ($event: any) => (_unref(requestOpenChat)(item.conv.id))
                    }, [...(_cache[15] || (_cache[15] = [
                      _createElementVNode("svg", {
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round"
                      }, [
                        _createElementVNode("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
                      ], -1),
                      _createTextVNode(" Ver Conversa ", -1)
                    ]))], 10, _hoisted_27)
                  ])
                ], 2))
              }), 128))
            ])
          ], 64))
  ], 2))
}
}

})